import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';
import { type ReaderFragment } from 'relay-runtime';

import { fg } from '@confluence/feature-gating';

import { isRelayEnabled } from './isRelayEnabled.ts';
import {
	syncFragmentCacheBridge,
	type FragmentCacheBridgeSource,
} from './cacheBridge/fragmentCacheBridgeSync.ts';
import { recordOptimisticFragmentWrite } from './optimisticInterop/recordOptimisticFragmentWrite.ts';
import {
	addTypenameToData as addTypenameToDataFromOperations,
	type WithTypename as FragmentWithTypename,
	writeFragmentShim,
} from './fragmentCacheOperations.ts';
import { logRelayMigrationDiagnostic } from './relayDiagnostics.ts';
import { getRelayFragmentIdKind, getRelayFragmentMetadata } from './relayFragment.ts';
import { toDocumentNode } from './toDocumentNode.ts';

export type WithTypename<T> = FragmentWithTypename<T>;

export const addTypenameToData = <TData extends Record<string, any>>(
	data: TData,
	fragmentArtifact: ReaderFragment,
): WithTypename<TData> => addTypenameToDataFromOperations(data, fragmentArtifact);

export type UseWriteFragmentInteropArgs = {
	fragmentSpec: GraphQLTaggedNode;
	/** Fragment artifact for adding __typename to data. __typename will be automatically added. */
	fragmentArtifact: ReaderFragment;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseWriteFragmentInteropResult<TData> = (params: {
	fragmentId: string;
	data: Omit<WithTypename<TData>, ' $fragmentType'>;
	variables?: Record<string, any>;
	useAggClient?: boolean;
}) => void;

function useWriteFragmentInteropRelay<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec, fragmentArtifact, __callerPackage__ } = args;
	const env = useRelayEnvironment();

	return useCallback(
		({ data, fragmentId, variables, useAggClient }) => {
			if (fg('confluence_relay_migration_diagnostics_enabled')) {
				const { fragmentName, fragmentType } = getRelayFragmentMetadata(fragmentSpec);
				logRelayMigrationDiagnostic('Fragment access', {
					phase: 'fragment',
					action: 'write',
					api: 'useWriteFragmentInterop',
					callerPackage: __callerPackage__,
					fragmentName,
					fragmentType,
					fragmentIdKind: getRelayFragmentIdKind(fragmentId),
					relayEnabled: true,
				});
			}

			recordOptimisticFragmentWrite({
				fragment: fragmentSpec,
				fragmentId,
				data,
				variables,
				useAggClient,
				callerPackage: __callerPackage__,
				writeFragment: writeFragmentInterop,
			});

			const didWrite = writeFragmentShim(env, fragmentSpec, fragmentId, data, variables);

			if (didWrite) {
				// Mirror only after the original Relay write actually changed the store.
				syncFragmentCacheBridge({
					source: 'relay',
					fragment: fragmentSpec,
					fragmentId,
					data,
					variables,
					useAggClient,
					relayEnvironment: env,
					fragmentArtifact,
				});
			}
		},
		[env, fragmentSpec, fragmentArtifact, __callerPackage__],
	);
}

function useWriteFragmentInteropApollo<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec, fragmentArtifact, __callerPackage__ } = args;
	const apolloClient = useApolloClient();

	return useCallback(
		({ data, fragmentId, variables, useAggClient }) => {
			if (fg('confluence_relay_migration_diagnostics_enabled')) {
				const { fragmentName, fragmentType } = getRelayFragmentMetadata(fragmentSpec);
				logRelayMigrationDiagnostic('Fragment access', {
					phase: 'fragment',
					action: 'write',
					api: 'useWriteFragmentInterop',
					callerPackage: __callerPackage__,
					fragmentName,
					fragmentType,
					fragmentIdKind: getRelayFragmentIdKind(fragmentId),
					relayEnabled: false,
				});
			}

			// Add __typename to data using the fragment artifact's type information
			const dataToWrite = addTypenameToData(
				data as Record<string, any>,
				fragmentArtifact,
			) as unknown as TData;

			recordOptimisticFragmentWrite({
				fragment: fragmentSpec,
				fragmentId,
				data: dataToWrite,
				variables,
				useAggClient,
				callerPackage: __callerPackage__,
				writeFragment: writeFragmentInterop,
			});

			// Guard window for server usage while preserving Apollo as the original write target.
			const client =
				useAggClient && typeof window !== 'undefined'
					? window.GLOBAL_APOLLO_AGG_CLIENT
					: apolloClient;

			if (client) {
				client.writeFragment<TData>({
					id: fragmentId,
					fragment: toDocumentNode(fragmentSpec),
					data: dataToWrite as TData,
					variables,
				});

				// The Apollo write above is the original operation; the bridge mirrors
				// the same shaped fragment data into Relay as secondary work.
				syncFragmentCacheBridge({
					source: 'apollo',
					fragment: fragmentSpec,
					fragmentId,
					data: dataToWrite,
					variables,
					useAggClient,
					apolloClient: client,
					fragmentArtifact,
				});
			}
		},
		[apolloClient, fragmentSpec, fragmentArtifact, __callerPackage__],
	);
}

export function useWriteFragmentInterop<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<Omit<TData, ' $fragmentType'>> {
	return isRelayEnabled(args.__callerPackage__, { phase: 'fragment' })
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropRelay(args)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropApollo(args);
}

export type WriteFragmentInteropOptions<TData> = {
	fragmentId: string;
	data: TData;
	fragment?: GraphQLTaggedNode;
	variables?: Record<string, any>;
	useAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

/**
 * Imperative write fragment for use outside of React components (e.g. in mutation `update` callbacks).
 * Mirrors the shape of `useWriteFragmentInterop` but uses global clients directly instead of hooks,
 * analogous to how `readFragmentInterop` is the imperative version of `useReadFragmentInterop`.
 *
 * In the Relay build variant, uses `window.GLOBAL_RELAY_ENV` and `writeFragmentShim`.
 * In the Apollo build variant, uses `window.GLOBAL_APOLLO_CLIENT` (or `window.GLOBAL_APOLLO_AGG_CLIENT`)
 * and `client.writeFragment`.
 *
 * @param options.fragmentId - The Apollo cache key (e.g. `TypeName:id` or `$ROOT_QUERY.fieldName`)
 * @param options.data - The data to write into the cache
 * @param options.fragment - Optional GraphQL fragment. Required for Apollo build variant to correctly
 *   write shaped data. In Relay build variant, the fragment selections are used to traverse fields.
 * @param options.variables - Optional variables for parameterised fragments
 * @param options.useAggClient - If true, uses the AGG Apollo client instead of the default client
 */
export function writeFragmentInterop<TData>(options: WriteFragmentInteropOptions<TData>): void {
	const { fragmentId, data, fragment, variables, useAggClient, __callerPackage__ } = options;

	if (!data) {
		return;
	}

	const source: FragmentCacheBridgeSource = isRelayEnabled(__callerPackage__, {
		phase: 'fragment',
	})
		? 'relay'
		: 'apollo';
	if (fg('confluence_relay_migration_diagnostics_enabled')) {
		const { fragmentName, fragmentType } = getRelayFragmentMetadata(fragment);
		logRelayMigrationDiagnostic('Fragment access', {
			phase: 'fragment',
			action: 'write',
			api: 'writeFragmentInterop',
			callerPackage: __callerPackage__,
			fragmentName,
			fragmentType,
			fragmentIdKind: getRelayFragmentIdKind(fragmentId),
			relayEnabled: source === 'relay',
		});
	}

	if (source === 'relay') {
		const env = window['GLOBAL_RELAY_ENV']!;
		if (fragment) {
			recordOptimisticFragmentWrite({
				fragment,
				fragmentId,
				data,
				variables,
				useAggClient,
				callerPackage: __callerPackage__,
				writeFragment: writeFragmentInterop,
			});

			const didWrite = writeFragmentShim(env, fragment, fragmentId, data, variables);
			if (didWrite) {
				// Mirror only after the original Relay write actually changed the store.
				syncFragmentCacheBridge({
					source,
					fragment,
					fragmentId,
					data,
					variables,
					useAggClient,
					relayEnvironment: env,
					fragmentArtifact: fragment as ReaderFragment,
				});
			}
		}
		return;
	}

	const client =
		useAggClient && typeof window !== 'undefined'
			? window['GLOBAL_APOLLO_AGG_CLIENT']
			: window['GLOBAL_APOLLO_CLIENT'];

	if (client && fragment) {
		const dataToWrite = addTypenameToDataFromOperations(
			data as Record<string, any>,
			fragment as ReaderFragment,
		) as unknown as TData;

		recordOptimisticFragmentWrite({
			fragment,
			fragmentId,
			data: dataToWrite,
			variables,
			useAggClient,
			callerPackage: __callerPackage__,
			writeFragment: writeFragmentInterop,
		});

		client.writeFragment<TData>({
			id: fragmentId,
			fragment: toDocumentNode(fragment),
			data: dataToWrite,
			variables,
		});

		// The Apollo write above is the original operation; the bridge mirrors
		// the same shaped fragment data into Relay as secondary work.
		syncFragmentCacheBridge({
			source,
			fragment,
			fragmentId,
			data: dataToWrite,
			variables,
			useAggClient,
			apolloClient: client,
			fragmentArtifact: fragment as ReaderFragment,
		});
	}
}
